import { RecurrenceType, ScheduledNotification } from '@vyce/core/src/types';

export interface SnapshotFormValue {
  daysOption: string;
  sendDay: { name: string; uuid: string } | null;
  sendTime: Date | null;
  receivers: { name: string; uuid: string }[];
  selectedLocationIds: string[];
  notificationId?: string;
  isActive?: boolean;
  name?: string;
}

export const EMPTY_SNAPSHOT: SnapshotFormValue = {
  daysOption: 'every_day',
  sendDay: null,
  sendTime: null,
  receivers: [],
  selectedLocationIds: [],
};

export interface ScheduledNotificationWithId extends ScheduledNotification {
  id: string;
}

const DAY_INDEX_MAP: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

const DAYS_OPTION_TO_RECURRENCE: Record<string, RecurrenceType> = {
  every_day: RecurrenceType.EVERY_DAY,
  every_weekday: RecurrenceType.EVERY_WEEKDAY,
  select_days: RecurrenceType.CUSTOM,
};

export const formSnapshotToScheduledNotification = (
  snapshot: SnapshotFormValue,
  name: string,
  locationsCount: number,
  channel: 'email' | 'sms' = 'email'
): ScheduledNotification => {
  const allLocations = locationsCount > 0 && snapshot.selectedLocationIds.length === locationsCount;
  const recurrenceType = DAYS_OPTION_TO_RECURRENCE[snapshot.daysOption] ?? RecurrenceType.EVERY_DAY;

  let customDays: number[] = [];
  if (recurrenceType === RecurrenceType.CUSTOM && snapshot.sendDay) {
    const dayIndex = DAY_INDEX_MAP[snapshot.sendDay.name] ?? parseInt(snapshot.sendDay.uuid, 10);
    customDays = [dayIndex];
  }

  const sendTime = snapshot.sendTime
    ? `${String(snapshot.sendTime.getHours()).padStart(2, '0')}:00`
    : '08:00';

  const recipientUserIds = (snapshot.receivers ?? [])
    .map(r => (typeof r === 'string' ? r : r.uuid))
    .filter(Boolean);

  return {
    channel,
    name,
    config: {
      all_locations: allLocations,
      location_ids: snapshot.selectedLocationIds,
      recurrence_type: recurrenceType,
      custom_days: customDays,
      send_time: sendTime,
      recipient_emails: [],
      recipient_user_ids: recipientUserIds,
    },
  };
};

export const scheduledNotificationToFormSnapshot = (
  notification: ScheduledNotification | Record<string, unknown>,
  allLocationIds: string[] = [],
  recipientIdToName: Record<string, string> = {}
): SnapshotFormValue => {
  const config = (notification as ScheduledNotification).config;
  if (!config) return { ...EMPTY_SNAPSHOT };

  const dayIndex = config.custom_days?.[0] ?? 1;
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  let daysOption = 'every_day';
  if (config.recurrence_type === RecurrenceType.EVERY_WEEKDAY) {
    daysOption = 'every_weekday';
  } else if (config.recurrence_type === RecurrenceType.CUSTOM) {
    daysOption = 'select_days';
  }

  const [hours] = (config.send_time || '08:00').split(':').map(Number);
  const sendTime = new Date();
  sendTime.setHours(hours, 0, 0, 0);

  const selectedLocationIds = config.all_locations ? allLocationIds : config.location_ids ?? [];

  const notif = notification as ScheduledNotification;

  return {
    daysOption,
    sendDay: { name: dayNames[dayIndex], uuid: String(dayIndex) },
    sendTime,
    name: notif.name,
    receivers:
      config.recipient_user_ids?.map(id => ({
        uuid: id,
        name: recipientIdToName[id] ?? id,
      })) ?? [],
    selectedLocationIds,
  };
};
